import { useEffect } from 'react';
import { useSession } from '../auth/session.tsx';
import { useMe } from '../api/queries.ts';
import { postSeen, type Task } from '../api/taskQueries.ts';
import { batches, pendingSeen } from './seenPure.ts';

export { assignmentKey, batches, pendingSeen, toMarkSeen } from './seenPure.ts';

/**
 * "Seen" (ruling 17): the FIRST time a task is shown to its current holder on any screen, the dashboard's first
 * twelve included (Code 0, 23 October). Only rows actually drawn count, and only the viewer's own: a director looking
 * at a colleague's task, or at the queue, sees nothing on anyone's behalf. The server records it once per task per
 * ASSIGNMENT (A-#79): a task that comes back to someone is seen afresh. It ignores the rest, so a repeat is harmless;
 * this keeps a screen from asking twice for the same assignment.
 */

// Asked already in this page's life, per task AND assignment (its assigned_at), so a re-render or a refetch that still
// says null does not ask again, while a task given back to the reader later is asked for afresh.
const asked = new Set<string>();

/** Call with exactly the tasks the screen draws. One call per new set, never per row. */
export function useSeen(shown: readonly Task[] | undefined) {
  const me = useMe();
  const { getToken } = useSession();
  const key = pendingSeen(shown ?? [], me.data?.id, asked).keys.join(',');
  useEffect(() => {
    if (!key) return;
    // Re-checked here, not only when the key was made: an effect can run twice for one render (React's development
    // mode mounts twice), and a holding already sent is never sent again.
    const keys = key.split(',').filter((k) => !asked.has(k));
    if (keys.length === 0) return;
    for (const k of keys) asked.add(k);
    const fresh = keys.map((k) => k.slice(0, k.indexOf('@')));
    for (const [i, chunk] of batches(fresh).entries()) {
      // Seen is a record, not a thing the reader acts on: a failure is not shown, and the ids may be asked again.
      const mine = keys.slice(i * 500, i * 500 + chunk.length);
      void postSeen(getToken, chunk).catch(() => { for (const k of mine) asked.delete(k); });
    }
  }, [key, getToken]);
}

/** For tests only: forget what this page has asked. */
export function forgetSeen(): void { asked.clear(); }
